import React, { useEffect, useRef, useState } from 'react';

function truncateMiddle(value: string, head = 8, tail = 6): string {
  if (!value || value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface CopyableIdProps {
  value: string;
  /** Accessible name, e.g. "Client ID" or "Contract address" */
  label?: string;
  head?: number;
  tail?: number;
  /** When false, show the full value on one line (no middle ellipsis). */
  truncate?: boolean;
  className?: string;
}

const CopyableId: React.FC<CopyableIdProps> = ({
  value,
  label = 'ID',
  head = 8,
  tail = 6,
  truncate = true,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  if (!value) {
    return <span className="dash-address dash-address-empty">—</span>;
  }

  const showCopied = () => {
    setCopied(true);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 1600);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (ok) showCopied();
  };

  const display = truncate ? truncateMiddle(value, head, tail) : value;

  return (
    <button
      type="button"
      className={`dash-address${copied ? ' dash-address-copied' : ''} ${className}`.trim()}
      title={copied ? 'Copied!' : `Click to copy ${label}`}
      aria-label={
        copied ? `${label} copied to clipboard` : `${label} ${value}. Click to copy.`
      }
      onClick={handleCopy}
    >
      <span className="dash-address-text">{display}</span>
      <svg
        className={`dash-address-icon${copied ? ' dash-address-icon-copied' : ''}`}
        viewBox="0 0 24 24"
        width="12"
        height="12"
        aria-hidden
      >
        {copied ? (
          <path
            fill="currentColor"
            d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
          />
        ) : (
          <path
            fill="currentColor"
            d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
          />
        )}
      </svg>
    </button>
  );
};

export default CopyableId;
